import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { FiUser, FiUsers, FiTrendingUp, FiLayers, FiAlertTriangle, FiChevronRight, FiChevronDown, FiDownload } from "react-icons/fi";
import * as XLSX from "xlsx";
import moment from "moment";
import MainCard from "../../../component/MainCard/MainCard";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import ProfileTabs from "../../../component/Tabs/Tabs";
import useLoadingIndicator from "../../../component/Loader/Loader";
import * as actions from "../../../Redux/Actions";
import { showErrorToastMessage } from "../../../component/Toasts/Toasts";
import "./CostDashboard.scss";

const formatCurrency = (value: number) =>
  `₹${Math.round(value).toLocaleString("en-IN")}`;

type TabKey = "employee" | "plan" | "businessUnit";

const PLAN_TYPE_LABEL: Record<string, string> = { health: "Health (Main)", opd: "OPD", life: "Life" };

// Writes one sheet of plain rows plus a TOTAL row (live SUM formula) under the given money column.
const exportRows = (rows: Record<string, any>[], sheetName: string, fileName: string, moneyColumn: string) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const header = Object.keys(rows[0]);
  const moneyIndex = header.indexOf(moneyColumn);
  const moneyCol = XLSX.utils.encode_col(moneyIndex);
  const lastDataRow = rows.length + 1;
  for (let r = 2; r <= lastDataRow; r++) {
    if (ws[`${moneyCol}${r}`]) ws[`${moneyCol}${r}`].z = "#,##0.00";
  }
  const totalRow = lastDataRow + 1;
  ws[`A${totalRow}`] = { t: "s", v: "TOTAL" };
  ws[`${moneyCol}${totalRow}`] = {
    t: "n",
    f: `SUM(${moneyCol}2:${moneyCol}${lastDataRow})`,
    v: rows.reduce((sum, row) => sum + (Number(row[moneyColumn]) || 0), 0),
    z: "#,##0.00",
  };
  ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: totalRow - 1, c: header.length - 1 } });
  ws["!cols"] = header.map((h) => ({
    wch: Math.min(45, Math.max(h.length + 2, ...rows.slice(0, 500).map((row) => String(row[h] ?? "").length + 2))),
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}_${moment().format("YYYY-MM-DD")}.xlsx`);
};

// Onsurity Insurance Cost Dashboard — org-wide rollup of what Skillmine is paying, computed
// locally from a rate table derived from Onsurity's own billing export (not a live Onsurity
// API call). See docs/onsurity-cost-dashboard.md in spotlight-sdk for the full data analysis,
// and OnsurityCostService for what's not yet priced (some age bands of Emerald/Heliodor).
const CostDashboard = () => {
  const dispatch = useDispatch();
  const { loadingComponent, showLoadingIndicator, hideLoadingIndicator } = useLoadingIndicator();
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>("employee");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [planSearch, setPlanSearch] = useState("");
  const [buSearch, setBuSearch] = useState("");
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [showEmployeeExport, setShowEmployeeExport] = useState(false);

  useEffect(() => {
    showLoadingIndicator();
    dispatch(
      actions.getOnsurityCostSummaryAction((res: any) => {
        setLoading(false);
        hideLoadingIndicator();
        if (res?.error) {
          showErrorToastMessage(res.error.error_description || "Couldn't load the cost dashboard.");
        } else {
          setSummary(res?.data || null);
        }
      })
    );
  }, []);

  const byPlanRows = useMemo(() => {
    if (!summary?.byPlan) return [];
    const rows = Object.entries(summary.byPlan).map(([planName, total]) => ({ planName, total: total as number }));
    return rows.sort((a, b) => b.total - a.total);
  }, [summary]);

  const filteredPlanRows = useMemo(() => {
    if (!planSearch.trim()) return byPlanRows;
    const q = planSearch.trim().toLowerCase();
    return byPlanRows.filter((r) => r.planName.toLowerCase().includes(q));
  }, [byPlanRows, planSearch]);

  const byBuRows = useMemo(() => {
    if (!summary?.byBusinessUnit) return [];
    const rows = Object.entries(summary.byBusinessUnit).map(([bu, total]) => ({ bu, total: total as number }));
    return rows.sort((a, b) => b.total - a.total);
  }, [summary]);

  const filteredBuRows = useMemo(() => {
    if (!buSearch.trim()) return byBuRows;
    const q = buSearch.trim().toLowerCase();
    return byBuRows.filter((r) => r.bu.toLowerCase().includes(q));
  }, [byBuRows, buSearch]);

  const filteredEmployees = useMemo(() => {
    const rows = summary?.perEmployee || [];
    if (!employeeSearch.trim()) return rows;
    const q = employeeSearch.trim().toLowerCase();
    return rows.filter(
      (r: any) =>
        r.name?.toLowerCase().includes(q) ||
        r.employeeId?.toLowerCase?.().includes(q) ||
        r.businessUnit?.toLowerCase().includes(q)
    );
  }, [summary, employeeSearch]);

  // Exports exactly what's on screen (search applied), one row per person per plan in the same
  // order as the expanded view: employee's main plan, employee's OPD, then each family member.
  const exportEmployees = (includeDependents: boolean) => {
    const rows = filteredEmployees.flatMap((emp: any) =>
      (emp.lineItems || [])
        .filter((item: any) => includeDependents || item.relation === "Self")
        .map((item: any) => ({
          "Employee ID": emp.employeeId,
          "Employee Name": emp.name,
          "Email": emp.emailId || "",
          "Business Unit": emp.businessUnit || "",
          // Employees-only rows are all "Self", so Member Name/Relation would just repeat the employee.
          ...(includeDependents && {
            "Member Name": item.name,
            "Relation": String(item.relation || "").replace(/_/g, "-"),
          }),
          "Age Band": item.ageBand || "",
          "Plan Type": PLAN_TYPE_LABEL[item.planType] || item.planType,
          "Plan Name": item.planName,
          "Monthly Cost (₹)": item.price ?? 0,
        }))
    );
    setShowEmployeeExport(false);
    if (!rows.length) {
      showErrorToastMessage("Nothing to export.");
      return;
    }
    exportRows(
      rows,
      "Per-Employee Cost",
      includeDependents ? "Onsurity_Cost_Employees_With_Dependents" : "Onsurity_Cost_Employees_Only",
      "Monthly Cost (₹)"
    );
  };

  const exportPlans = () =>
    exportRows(
      filteredPlanRows.map((r) => ({ "Plan": r.planName, "Monthly Cost (₹)": r.total })),
      "Cost by Plan",
      "Onsurity_Cost_By_Plan",
      "Monthly Cost (₹)"
    );

  const exportBusinessUnits = () =>
    exportRows(
      filteredBuRows.map((r) => ({ "Business Unit": r.bu, "Monthly Cost (₹)": r.total })),
      "Cost by Business Unit",
      "Onsurity_Cost_By_Business_Unit",
      "Monthly Cost (₹)"
    );

  return (
    <MainCard>
      {loadingComponent()}
      <div className="assessment-heading-button-wrapper">
        <div>
          <h2>Onsurity Insurance Cost Dashboard</h2>
        </div>
      </div>

      {loading ? null : !summary ? (
        <p className="empty-note mt-4">Couldn't load cost data.</p>
      ) : (
        <>
          {summary.hasUnknownPrices && (
            <div className="cost-dashboard-warning mt-3">
              <FiAlertTriangle size={16} />
              Some employees include a plan/age combination we don't have a confirmed price for
              yet — their totals (and the org-wide total) are undercounted until those rates are
              confirmed. See <code>docs/onsurity-cost-open-questions.md</code>.
            </div>
          )}

          <div className="dash-tiles mt-4">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#e7eefb", color: "#1853a2" }}>
                <FiUsers size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{summary.employeeCount}</span>
                <span className="stat-label">Active Enrolled Employees</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#e2f2e9", color: "#1f9254" }}>
                <FiTrendingUp size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{formatCurrency(summary.total)}</span>
                <span className="stat-label">Total Monthly Cost</span>
                <span className="stat-sub">across all active plans</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#fdf2d8", color: "#b7860b" }}>
                <FiLayers size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{byPlanRows.length}</span>
                <span className="stat-label">Plans in Use</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#e7eefb", color: "#1853a2" }}>
                <FiTrendingUp size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">
                  {summary.employeeCount ? formatCurrency(summary.total / summary.employeeCount) : "—"}
                </span>
                <span className="stat-label">Avg. Cost / Employee</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#e2f2e9", color: "#1f9254" }}>
                <FiUser size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{formatCurrency(summary.employeeOnlyTotal)}</span>
                <span className="stat-label">Employees' Own Cost</span>
                <span className="stat-sub">just the "Self" line items</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#fdf2d8", color: "#b7860b" }}>
                <FiUsers size={20} />
              </div>
              <div className="stat-text">
                <span className="stat-value">{formatCurrency(summary.dependentTotal)}</span>
                <span className="stat-label">Dependents' Cost</span>
                <span className="stat-sub">spouse, children, parents, etc.</span>
              </div>
            </div>
          </div>

          <div className="cost-dashboard-tabs mt-4">
            <ProfileTabs
              title="Per-Employee Cost"
              onClick={() => setActiveTab("employee")}
              active={activeTab === "employee"}
            />
            <ProfileTabs
              title="Cost by Plan"
              onClick={() => setActiveTab("plan")}
              active={activeTab === "plan"}
            />
            <ProfileTabs
              title="Cost by Business Unit"
              onClick={() => setActiveTab("businessUnit")}
              active={activeTab === "businessUnit"}
            />
          </div>

          {activeTab === "employee" && (
            <div className="readable-table mt-3">
              <div className="cost-dashboard-table-header">
                <h5>
                  Per-Employee Cost<span className="count-chip">{filteredEmployees.length}</span>
                </h5>
                <div className="cost-dashboard-header-actions">
                  <input
                    type="text"
                    className="form-control cost-dashboard-search"
                    placeholder="Search by name, employee ID, or BU"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn cost-dashboard-export-btn"
                    disabled={filteredEmployees.length === 0}
                    onClick={() => setShowEmployeeExport(true)}
                  >
                    <FiDownload size={16} /> Export
                  </button>
                </div>
              </div>
              <div className="table table-responsive">
                <table className="table-main table table-sm mt-2">
                  <thead className="table-head">
                    <tr className="table-head-row">
                      <th className="table-head-row cost-dashboard-expand-col"></th>
                      <th className="table-head-row">E-Code</th>
                      <th className="table-head-row">Employee Name</th>
                      <th className="table-head-row">Email</th>
                      <th className="table-head-row">Business Unit</th>
                      <th className="table-head-row">Monthly Cost</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filteredEmployees.map((r: any) => (
                      <Fragment key={r.employeeId}>
                        <tr
                          className="table-body-row cost-dashboard-clickable-row"
                          onClick={() => setExpandedEmployeeId(expandedEmployeeId === r.employeeId ? null : r.employeeId)}
                        >
                          <td className="cost-dashboard-expand-col">
                            {expandedEmployeeId === r.employeeId ? (
                              <FiChevronDown size={16} />
                            ) : (
                              <FiChevronRight size={16} />
                            )}
                          </td>
                          <td>{r.employeeId}</td>
                          <td>{r.name}</td>
                          <td>{r.emailId || "-"}</td>
                          <td>{r.businessUnit || "-"}</td>
                          <td>
                            {formatCurrency(r.total)}
                            {r.hasUnknownPrices && (
                              <FiAlertTriangle
                                size={14}
                                className="cost-dashboard-row-warning"
                                title="Includes a plan/age combination with no confirmed price yet — undercounted."
                              />
                            )}
                          </td>
                        </tr>
                        {expandedEmployeeId === r.employeeId && (
                          <tr className="table-body-row" key={`${r.employeeId}-detail`}>
                            <td colSpan={6} className="cost-dashboard-expand-cell">
                              <table className="table-main table table-sm cost-dashboard-detail-table">
                                <thead className="table-head">
                                  <tr className="table-head-row">
                                    <th className="table-head-row">Person</th>
                                    <th className="table-head-row">Relation</th>
                                    <th className="table-head-row">Plan</th>
                                    <th className="table-head-row">Age Band</th>
                                    <th className="table-head-row">Cost</th>
                                  </tr>
                                </thead>
                                <tbody className="table-body">
                                  {r.lineItems.map((item: any, i: number) => (
                                    <tr className="table-body-row" key={i}>
                                      <td>{item.name}</td>
                                      <td>{item.relation}</td>
                                      <td>
                                        {item.planName} ({item.planType})
                                      </td>
                                      <td>{item.ageBand || "-"}</td>
                                      <td>
                                        {item.price !== undefined ? (
                                          formatCurrency(item.price)
                                        ) : (
                                          <span className="cost-dashboard-unknown" title="No confirmed rate for this plan/age band yet — shown as ₹0, not an actual confirmed cost.">
                                            {formatCurrency(0)} <FiAlertTriangle size={13} />
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                    {filteredEmployees.length === 0 && (
                      <tr className="table-body-row">
                        <td colSpan={6} className="empty-note">
                          No employees match "{employeeSearch}".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "plan" && (
            <div className="readable-table mt-3">
              <div className="cost-dashboard-table-header">
                <h5>
                  Cost by Plan<span className="count-chip">{filteredPlanRows.length}</span>
                </h5>
                <div className="cost-dashboard-header-actions">
                  <input
                    type="text"
                    className="form-control cost-dashboard-search"
                    placeholder="Search by plan name"
                    value={planSearch}
                    onChange={(e) => setPlanSearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn cost-dashboard-export-btn"
                    disabled={filteredPlanRows.length === 0}
                    onClick={exportPlans}
                  >
                    <FiDownload size={16} /> Export
                  </button>
                </div>
              </div>
              <div className="table table-responsive">
                <table className="table-main table table-sm mt-2">
                  <thead className="table-head">
                    <tr className="table-head-row">
                      <th className="table-head-row">Plan</th>
                      <th className="table-head-row">Monthly Cost</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filteredPlanRows.map((r) => (
                      <tr className="table-body-row" key={r.planName}>
                        <td>{r.planName}</td>
                        <td>{formatCurrency(r.total)}</td>
                      </tr>
                    ))}
                    {filteredPlanRows.length === 0 && (
                      <tr className="table-body-row">
                        <td colSpan={2} className="empty-note">
                          No plans match "{planSearch}".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "businessUnit" && (
            <div className="readable-table mt-3">
              <div className="cost-dashboard-table-header">
                <h5>
                  Cost by Business Unit<span className="count-chip">{filteredBuRows.length}</span>
                </h5>
                <div className="cost-dashboard-header-actions">
                  <input
                    type="text"
                    className="form-control cost-dashboard-search"
                    placeholder="Search by business unit"
                    value={buSearch}
                    onChange={(e) => setBuSearch(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn cost-dashboard-export-btn"
                    disabled={filteredBuRows.length === 0}
                    onClick={exportBusinessUnits}
                  >
                    <FiDownload size={16} /> Export
                  </button>
                </div>
              </div>
              <div className="table table-responsive">
                <table className="table-main table table-sm mt-2">
                  <thead className="table-head">
                    <tr className="table-head-row">
                      <th className="table-head-row">Business Unit</th>
                      <th className="table-head-row">Monthly Cost</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {filteredBuRows.map((r) => (
                      <tr className="table-body-row" key={r.bu}>
                        <td>{r.bu}</td>
                        <td>{formatCurrency(r.total)}</td>
                      </tr>
                    ))}
                    {filteredBuRows.length === 0 && (
                      <tr className="table-body-row">
                        <td colSpan={2} className="empty-note">
                          No business units match "{buSearch}".
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <ModalComponents
        show={showEmployeeExport}
        onHide={() => setShowEmployeeExport(false)}
        title="Export Per-Employee Cost"
        backdrop={true}
        keyboard={true}
        modalBody={
          <div className="cost-dashboard-export-modal">
            <p>
              Exporting <strong>{filteredEmployees.length}</strong> employee
              {filteredEmployees.length === 1 ? "" : "s"}
              {employeeSearch.trim() ? <> matching "{employeeSearch.trim()}"</> : null}. Include their
              family members?
            </p>
            <div className="cost-dashboard-export-options">
              <button type="button" className="btn add-submit-button buttonsize" onClick={() => exportEmployees(true)}>
                With Dependents
              </button>
              <button type="button" className="btn add-submit-button buttonsize" onClick={() => exportEmployees(false)}>
                Employees Only
              </button>
              <button type="button" className="btn cancel-btn buttonsize" onClick={() => setShowEmployeeExport(false)}>
                Cancel
              </button>
            </div>
          </div>
        }
      />
    </MainCard>
  );
};

export default CostDashboard;
